import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { HistoryLookupService } from '../historyLookup.service';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import * as HistoryLookupGridColumns from './historyLookupGridColumns.util';

@Component({
    selector: 'history-lookup-site-descriptor',
    template: `<ws-ag-grid-angular class="ag-theme-balham grid grid-cell-no-focus"
                    [gridOptions]="gridOptions"
                    (gridReady)="onAgGridReady($event)"
                    [gridId]="gridId"
                    [forceShowToolPanel]="true"
                    [showConfigureLayout]="false"
                    [defaultSizeToFit]="false"
                    [defaultSizeToContent]="true">
               </ws-ag-grid-angular>`,
    standalone: false,
})
export class HistoryLookupSiteDescriptorComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _historyLookupService: HistoryLookupService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _toastr: ToastrService,
    ) {}

    @Input({ required: true }) triggerSearch$!: Observable<Core.HistoryLookupSearchModel>;

    private triggerSearchSubscription: Subscription = new Subscription();

    readonly TABLE_NAME: string = 'SiteDescriptor';
    indexedHistoryTableId: number = -1;
    gridId: System.Guid = '8618239F-BA23-499F-A5CB-664673CEDD2E';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .build();
    gridApi!: GridApi;
    loadingGrid: boolean = false;
    gridResults: Core.HistoryResultSiteDescriptorModel[] = [];

    async ngOnInit(): Promise<void> {
        this.indexedHistoryTableId =
            this._historyLookupService.tableOptions.find((x) => x.historyTableName === this.TABLE_NAME)?.indexedHistoryTableId ?? -1;

        this.triggerSearchSubscription = this.triggerSearch$.subscribe(async (searchModel: Core.HistoryLookupSearchModel) => {
            await this.doSearch(searchModel);
        });
    }

    ngOnDestroy(): void {
        if (this.triggerSearchSubscription) {
            this.triggerSearchSubscription.unsubscribe();
        }
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = this.getGridColumns();
        this.gridApi.setGridOption('columnDefs', columns);

        this.gridApi.setGridOption('loading', false);
        this.gridApi.setGridOption('rowData', []);
    }

    readyToSearch(searchModel: Core.HistoryLookupSearchModel): boolean {
        if (searchModel.indexedHistoryTableId <= 0 || searchModel.indexedHistoryColumnId <= 0 || !searchModel.columnIdValues) {
            return false;
        }

        return searchModel.columnIdValues.every((value) => value > 0);
    }

    async doSearch(searchModel: Core.HistoryLookupSearchModel): Promise<void> {
        if (!this.readyToSearch(searchModel)) {
            this._toastr.error(
                'Please select a table and column, and enter values for all columns before searching.',
                'Cannot Search',
            );
            return;
        }
        if (searchModel.indexedHistoryTableId !== this.indexedHistoryTableId) {
            return;
        }
        if (!this.gridApi) {
            return;
        }

        this.loadingGrid = true;
        this.gridApi.setGridOption('loading', true);

        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            this.gridResults = await this._historyLookupService.historyLookupSiteDescriptor(searchModel);
            this._refreshRowData();
        } catch (error) {
            if ((error as { status?: number }).status !== 403) {
                this._toastr.error('An unexpected error occurred while fetching history data.', 'Search Error');
            }
            console.warn(error);
        } finally {
            busyRef.hide();
            this.gridApi.setGridOption('loading', false);
            this.loadingGrid = false;
        }
    }

    private _refreshRowData(): void {
        if (!this.gridApi) {
            return;
        }

        const columns = this.getGridColumns();
        this.gridApi.setGridOption('columnDefs', columns);

        this.gridApi.setGridOption('rowData', this._getRowData());
    }

    private _getRowData(): Core.HistoryResultSiteDescriptorModel[] {
        return _.chain(this.gridResults).cloneDeep().orderBy(['historyID'], ['desc']).value();
    }

    private getGridColumns(): ColDef[] {
        let cols = this._historyLookupService.getBaseHistoryColumnDefs();

        cols = [...cols, ...this._getSpecificColumnDefs()];

        return cols;
    }

    private _getSpecificColumnDefs(): ColDef[] {
        return [
            HistoryLookupGridColumns.numberColumn('SiteID', 'siteID'),
            HistoryLookupGridColumns.numberColumn('DescriptorID', 'descriptorID'),
            HistoryLookupGridColumns.textColumn('Value', 'value'),
            HistoryLookupGridColumns.textColumn('TransferID', 'transferId'),
            HistoryLookupGridColumns.textColumn('ActivityCorrelationId', 'activityCorrelationId'),
            HistoryLookupGridColumns.timestampColumn<Core.HistoryResultSiteDescriptorModel>('ChangeDate', 'changeDate', this._timestampPipe),
            HistoryLookupGridColumns.textColumn('ChangedBy', 'changedBy'),
        ];
    }
}
